import type { CallRecord, CallStatus, Chat } from '../types';
import { ACTIVE_CALL_STATES, TERMINAL_CALL_STATES } from '../types';

export type PriorityBucket = 'low' | 'normal' | 'high' | 'vip';

export function priorityBucket(priority: number): PriorityBucket {
  if (priority >= 80) return 'vip';
  if (priority >= 60) return 'high';
  if (priority >= 25) return 'normal';
  return 'low';
}

export function priorityLabel(priority: number): string | undefined {
  const bucket = priorityBucket(priority);
  switch (bucket) {
    case 'vip':
      return 'VIP';
    case 'high':
      return 'Hoog';
    case 'normal':
      return 'Normaal';
    case 'low':
    default:
      return undefined;
  }
}

export function priorityClassName(priority: number): string {
  return `priority-${priorityBucket(priority)}`;
}

export const statusLabels: Record<CallStatus, string> = {
  ringing: 'Belt',
  queued: 'In wachtrij',
  accepted: 'Geaccepteerd',
  connecting: 'Verbinden',
  connected: 'In gesprek',
  on_hold: 'In de wacht',
  transferred: 'Doorgeschakeld',
  ended: 'Beëindigd',
  missed: 'Gemist',
  rejected: 'Geweigerd',
  cancelled: 'Geannuleerd',
  failed: 'Mislukt',
};

export const endReasonLabels: Record<string, string> = {
  normal: 'Normaal',
  caller_hangup: 'Beller opgehangen',
  operator_hangup: 'Operator opgehangen',
  ai_hangup: 'AI opgehangen',
  timeout: 'Time-out',
  rejected: 'Geweigerd',
  missed: 'Gemist',
  cancelled: 'Geannuleerd',
  failed: 'Mislukt',
  transferred: 'Doorgeschakeld',
};

export const routeLabels: Record<CallRecord['route'], string> = {
  vip_immediate: 'VIP - Direct',
  auto_voicemail: 'Voicemail',
  queue_normal: 'Wachtrij',
};

export function callStatusClass(status: CallStatus): string {
  switch (status) {
    case 'ringing':
    case 'queued':
      return 'status-pending';
    case 'accepted':
    case 'connecting':
    case 'connected':
      return 'status-active';
    case 'on_hold':
      return 'status-hold';
    case 'transferred':
      return 'status-transfer';
    case 'ended':
      return 'status-ended';
    case 'missed':
    case 'rejected':
    case 'cancelled':
    case 'failed':
      return 'status-terminal';
    default:
      return '';
  }
}

export type CallAction =
  | 'accept'
  | 'reject'
  | 'join'
  | 'start_avatar'
  | 'stop_avatar'
  | 'takeover'
  | 'hold'
  | 'resume'
  | 'transfer'
  | 'end'
  | 'call_back'
  | 'details';

export function actionsForStatus(status: CallStatus): CallAction[] {
  switch (status) {
    case 'ringing':
    case 'queued':
      return ['accept', 'reject', 'details'];
    case 'accepted':
    case 'connecting':
      return ['join', 'start_avatar', 'stop_avatar', 'takeover', 'hold', 'transfer', 'end', 'details'];
    case 'connected':
      return ['join', 'stop_avatar', 'takeover', 'hold', 'transfer', 'end', 'details'];
    case 'on_hold':
      return ['resume', 'end', 'details'];
    case 'transferred':
      return ['end', 'details'];
    case 'ended':
    case 'missed':
    case 'rejected':
    case 'cancelled':
    case 'failed':
      return ['call_back', 'details'];
    default:
      return ['details'];
  }
}

export function isActiveCallStatus(status: CallStatus): boolean {
  return ACTIVE_CALL_STATES.has(status);
}

export function isTerminalCallStatus(status: CallStatus): boolean {
  return TERMINAL_CALL_STATES.has(status);
}

export function formatDuration(startedAt: string, endedAt?: string): string {
  const end = endedAt ? Date.parse(endedAt) : Date.now();
  const start = Date.parse(startedAt);
  if (Number.isNaN(end) || Number.isNaN(start) || end <= start) return '0:00';
  const totalSeconds = Math.floor((end - start) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatCallDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function callDirectionLabel(direction?: 'inbound' | 'outbound' | undefined): string {
  switch (direction) {
    case 'inbound':
      return 'Inkomend';
    case 'outbound':
      return 'Uitgaand';
    default:
      return 'Onbekend';
  }
}

export function channelLabel(isVideo: boolean): string {
  return isVideo ? '📹 Video' : '📞 Audio';
}

const normalizeIdentifier = (value: string): string =>
  value.replace(/\s/g, '').toLowerCase();

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const stripJidSuffix = (value: string): string =>
  value.replace(/@(c\.us|lid|g\.us|broadcast)$/i, '');

function identifierMatches(from: string, chatValue?: string): boolean {
  if (!chatValue) return false;
  const a = normalizeIdentifier(from);
  const b = normalizeIdentifier(chatValue);
  if (a === b) return true;
  if (stripJidSuffix(a) === stripJidSuffix(b)) return true;
  const aDigits = onlyDigits(a);
  const bDigits = onlyDigits(b);
  return aDigits.length > 0 && bDigits.length > 0 && aDigits === bDigits;
}

export function resolveCallChat(call: CallRecord, chats: Chat[]): Chat | undefined {
  return chats.find(
    (chat) =>
      chat.accountId === call.accountId &&
      (identifierMatches(call.from, chat.phone) ||
        identifierMatches(call.from, chat.externalId) ||
        identifierMatches(call.from, chat.participantId)),
  );
}

export function callerDisplayName(call: CallRecord, chats: Chat[]): string {
  const chat = resolveCallChat(call, chats);
  return chat?.title?.trim() || call.from;
}

export function callerInitials(call: CallRecord, chats: Chat[]): string {
  const name = callerDisplayName(call, chats);
  const words = name.split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return '?';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1];
  return `${first[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}
